// @ts-strict-ignore
import { IArticlePrepareProcessor } from './i-article-prepare.processor';
import { ArticleVO } from '@article/model/vo/article.vo';
import { SuitableConnectionsSetVO } from '../../model/vo/suitable-connections-set.vo';
import { ConnectionCubeSearchVector } from '@article/components/connections/connection-creation/search/connection-cube-search-vector';
import { ArticlePrepareData } from './insert/article-prepare-data';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { Dictionary } from 'typescript-collections';
import { ConnectionCubeSearchHelper } from '@article/components/connections/connection-creation/search/connection-cube-search.helper';
import { Connection } from '@3d-scene/model3d/connection';
import { InsertArticlePrepareData } from './insert/insert-article-prepare-data';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ConnectArticlePrepareData } from './insert/connect-article-prepare-data';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class DefaultMultipleConnectionPrepareProcessor implements IArticlePrepareProcessor {

  constructor(private articleAccessoryManager: ArticleAccessoryManager) { }

  process(article: ArticleVO, givingConnectionSets: SuitableConnectionsSetVO[], acceptingCubeVectors: ConnectionCubeSearchVector[],
    checkFreeConnection: boolean): ArticlePrepareData[] {
    const articlePrepareDatas: ArticlePrepareData[] = [];
    const cubeUidToConnectedArticlesCount: Dictionary<string, number> = new Dictionary<string, number>();
    for (const connectionSet of givingConnectionSets) {
      const cubeVector: ConnectionCubeSearchVector = ConnectionCubeSearchHelper.findCubeVectorById(connectionSet.searchVectorId,
        acceptingCubeVectors);
      if (cubeVector.searchForFreedomConnectionTarget) {
        // speicific case when found article (with multiple giving connections) should be connected to range-on-line (freedom) connection
        // such vectors contain only single accepting connection, these accepting connections are not included in other vectors
        // so additional validation in regard to multiplicity is not needed here
        for (const rangeOnLineCubeRequirements of cubeVector.sortedCubesSearchRequirements) {
          const rangeOnLineAcceptingCube: Connection = rangeOnLineCubeRequirements.getConnectionCube();
          if (rangeOnLineAcceptingCube) {
            const insertPrepareData: InsertArticlePrepareData = new InsertArticlePrepareData(connectionSet.searchVectorId,
              connectionSet.connectionNames);
            insertPrepareData.acceptingIdentityToInsert =
              MetaElementUsageIdentityVO.create(rangeOnLineAcceptingCube.articleContainer.articleGUID,
                rangeOnLineAcceptingCube.connectionName);
            insertPrepareData.rotationDelta =
              this.articleAccessoryManager.getRotationDelta(rangeOnLineAcceptingCube.articleContainer.article.identity, article.identity);
            articlePrepareDatas.push(insertPrepareData);
          }
        }
      } else {
        const acceptingCubes: Connection[] = ConnectionCubeSearchHelper.findAcceptingCubesByConnectionSet(connectionSet, cubeVector);
        if (acceptingCubes) {
          const connectionAllowed: boolean = checkFreeConnection ? this.isConnectionAllowed(acceptingCubes,
            cubeUidToConnectedArticlesCount) : true;
          if (connectionAllowed) {
            this.updateCurrentConnectedArticlesCount(acceptingCubes, cubeUidToConnectedArticlesCount);
            const connectPrepareData: ConnectArticlePrepareData = new ConnectArticlePrepareData(connectionSet.searchVectorId,
              connectionSet.connectionNames);
            connectPrepareData.acceptingConnections = acceptingCubes;
            articlePrepareDatas.push(connectPrepareData);
          }
        }
      }
    }
    return articlePrepareDatas;
  }

  private isConnectionAllowed(acceptingCubes: Connection[], cubeUidToConnectedArticlesCount: Dictionary<string, number>): boolean {
    for (const connectionCube of acceptingCubes) {
      const forwardArticlesCount: number = this.getCurrentConnectedArticlesCount(connectionCube, cubeUidToConnectedArticlesCount);
      if (!this.isConnectionByMultiplicityAllowed(connectionCube, forwardArticlesCount)) {
        return false;
      }
    }
    return true;
  }

  private isConnectionByMultiplicityAllowed(connectionCube: Connection, currentConnectedArticlesCount: number): boolean {
    const connectionArea: ConnectionAreaVO = connectionCube.metaElementVO as ConnectionAreaVO;
    if (connectionArea.connectionMultiplicity === 0) {
      return true; // number of possible connected articles is unlimited
    }
    return currentConnectedArticlesCount < connectionArea.connectionMultiplicity;
  }

  private getCurrentConnectedArticlesCount(connectionCube: Connection,
    cubeUidToConnectedArticlesCount: Dictionary<string, number>): number {
    if (!cubeUidToConnectedArticlesCount.containsKey(connectionCube.uid)) {
      cubeUidToConnectedArticlesCount.setValue(connectionCube.uid,
        MetaElementUsageUtils.forwardConnectedArticleUsageIds(connectionCube.metaElementUsage).length);
    }
    return cubeUidToConnectedArticlesCount.getValue(connectionCube.uid);
  }

  private updateCurrentConnectedArticlesCount(acceptingCubes: Connection[],
    cubeUidToConnectedArticlesCount: Dictionary<string, number>): void {
    for (const connectionCube of acceptingCubes) {
      let forwardArticlesCount: number = this.getCurrentConnectedArticlesCount(connectionCube, cubeUidToConnectedArticlesCount);
      forwardArticlesCount++;
      cubeUidToConnectedArticlesCount.setValue(connectionCube.uid, forwardArticlesCount);
    }
  }
}
